import React from 'react'

/** Container dimensions per size. The icon stays 16px at every size — only the
 *  tap target grows, so the same glyph reads identically across contexts. */
const ICON_PX = 16
const SIZE_CONFIG = {
  small: { container: 32, icon: ICON_PX },
  medium: { container: 40, icon: ICON_PX },
  large: { container: 48, icon: ICON_PX },
}

const NOTIFICATION_BADGE_PX = 8
/** Overlap onto the icon corner so the badge sits flush with the icon. */
const NOTIFICATION_BADGE_OVERLAP = 3

const COLOR_BRAND_04 = 'var(--color-brand-04)'
const COLOR_NEUTRAL_05 = 'var(--color-Neutral-5)'

const ButtonIcon = ({
  variant = 'normal',
  size = 'small',
  /** Overrides the glyph size for the chosen container, which otherwise stays 16px. */
  iconSize,
  isFocused = false,
  isDisabled = false,
  isLoading = false,
  hasNotification = false,
  cursorPointer = false,
  highlightOnHover = false,
  children = null,
  className = '',
  handleOnClick = () => {},
  ariaLabel,
  title,
  href = null,
  target,
  rel,
}) => {
  const iconPx = iconSize ?? (SIZE_CONFIG[size] ?? SIZE_CONFIG.small).icon

  const getSizeStyleClasses = () => {
    if (size === 'large') {
      return 'w-[48px] h-[48px]'
    }

    if (size === 'medium') {
      return 'w-[40px] h-[40px]'
    }

    return 'w-[32px] h-[32px]' /* small */
  }

  /** Circular whenever a hover fill can appear: transition-colors animates
   *  background-color but not border-radius, so a radius that only changed on
   *  hover would snap back to 8px while the fill was still fading out. */
  const radiusClass =
    highlightOnHover && !isDisabled
      ? 'rounded-[var(--radius-circular)]'
      : 'rounded-[var(--radius-sm)]'

  const getVariantStyleClasses = () => {
    if (variant !== 'outline') {
      return ' bg-transparent '
    }

    let classList = `${radiusClass} bg-surface-1`

    /* border hidden while loading */
    if (!isLoading) {
      const borderColor =
        isFocused && !isDisabled
          ? 'border-[var(--color-brand-04)]'
          : 'border-[var(--color-Neutral-3)]'
      classList += ` border border-solid border-[length:var(--border-hairline)] ${borderColor}`

      if (!isDisabled && !isFocused) {
        classList += ' hover:border-[var(--color-Neutral-6)]'
      }
    }

    return ` ${classList} `
  }

  const getIconFillColor = () => {
    if (isDisabled || isLoading) {
      return COLOR_NEUTRAL_05
    }

    if (isFocused) {
      return COLOR_BRAND_04
    }

    return COLOR_NEUTRAL_05
  }

  /** SVG icons set fill inline; use !important so hover/focus beat icon defaults (e.g. BellIcon → Neutral-6). */
  const getIconFillClasses = () => {
    if (isDisabled || isLoading) {
      return ''
    }

    if (isFocused) {
      return ' [&_svg]:!fill-[var(--color-brand-04)] [&_svg_path]:!fill-[var(--color-brand-04)]'
    }

    return ' [&_svg]:!fill-[var(--color-Neutral-5)] [&_svg_path]:!fill-[var(--color-Neutral-5)] hover:[&_svg]:!fill-[var(--color-Neutral-6)] hover:[&_svg_path]:!fill-[var(--color-Neutral-6)]'
  }

  const getClassList = () => {
    const defaultStyle = `flex items-center justify-center relative ${radiusClass} transition-colors `

    let classList =
      defaultStyle +
      getSizeStyleClasses() +
      getVariantStyleClasses() +
      getIconFillClasses() +
      ' ' +
      className

    if (isDisabled) {
      classList += ' opacity-25 '
    }

    if (isLoading) {
      classList += ' bg-surface-3 '
    }

    if (cursorPointer && !isDisabled) {
      classList += ' cursor-pointer '
    }

    if (highlightOnHover && !isDisabled) {
      classList += ' hover:bg-[var(--button-icon-hover-fill)] '
    }

    return classList
  }

  const renderIconChildren = () => {
    if (isLoading || children == null) return null

    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) return child

      return React.cloneElement(child, {
        width: iconPx,
        height: iconPx,
        fillcolor: getIconFillColor(),
        style: {
          ...child.props.style,
          width: iconPx,
          height: iconPx,
        },
      })
    })
  }

  const renderNotificationIndication = () => {
    const { container } = SIZE_CONFIG[size] ?? SIZE_CONFIG.small
    const iconInset = (container - iconPx) / 2
    const badgeOffset = iconInset + iconPx - NOTIFICATION_BADGE_OVERLAP

    return (
      <div
        className="pointer-events-none absolute rounded-full bg-[var(--color-informative2-4)]"
        style={{
          width: NOTIFICATION_BADGE_PX,
          height: NOTIFICATION_BADGE_PX,
          left: badgeOffset,
          top: badgeOffset,
        }}
        aria-hidden
      />
    )
  }

  const sharedProps = {
    'aria-busy': isLoading || undefined,
    'aria-label': ariaLabel,
    title,
    onClick: !isLoading && !isDisabled ? handleOnClick : undefined,
    className: getClassList() + ' p-0 outline-offset-2 focus-visible:outline-[var(--color-brand-04)]',
  }

  const content = (
    <>
      {renderIconChildren()}

      {!isLoading && hasNotification ? renderNotificationIndication() : ''}
    </>
  )

  /** Anchor variant keeps link semantics (middle-click, open in new tab) for navigational icons. */
  if (href && !isDisabled) {
    return (
      <a
        href={href}
        target={target}
        rel={rel ?? (target === '_blank' ? 'noreferrer' : undefined)}
        aria-disabled={isLoading || undefined}
        {...sharedProps}
      >
        {content}
      </a>
    )
  }

  return (
    <button type="button" disabled={isDisabled} {...sharedProps}>
      {content}
    </button>
  )
}

export default ButtonIcon
